import { createContext, useCallback, useContext, useEffect, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_KEY = "popupprofit:subscriberEmail";
const TOKEN_KEY = "popupprofit:authToken";

const ProContext = createContext({
  pro: false,
  email: null,
  expiresAt: null,
  loading: true,
  user: null,
  token: null,
  openUpgrade: () => {},
  closeUpgrade: () => {},
  upgradeOpen: false,
  refresh: async () => ({ pro: false }),
  saveEmail: () => {},
  clearEmail: () => {},
  register: async () => {},
  login: async () => {},
  logout: () => {},
});

const readLS = (k) => {
  try { return localStorage.getItem(k) || null; } catch { return null; }
};
const writeLS = (k, v) => {
  try { v == null ? localStorage.removeItem(k) : localStorage.setItem(k, v); } catch { /* ignore */ }
};

// Format FastAPI 422 detail (array of {msg,...}) into a string for the UI.
export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function ProProvider({ children }) {
  const [pro, setPro] = useState(false);
  const [email, setEmail] = useState(readLS(EMAIL_KEY));
  const [token, setToken] = useState(readLS(TOKEN_KEY));
  const [user, setUser] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const refresh = useCallback(async (overrideEmail) => {
    const e = (overrideEmail ?? readLS(EMAIL_KEY) ?? "").trim();
    if (!e) {
      setPro(false);
      setExpiresAt(null);
      return { pro: false };
    }
    try {
      const res = await fetch(`${API}/pro/status?email=${encodeURIComponent(e)}`);
      const data = await res.json();
      let playData = { pro: false };
      try { playData = await (await fetch(`${API}/play/status?email=${encodeURIComponent(e)}`)).json(); } catch (_) {}
      data.pro = data.pro || playData.pro;
      data.expires_at = data.expires_at || playData.expires_at;
      setPro(!!data.pro);
      setExpiresAt(data.expires_at || null);
      return data;
    } catch {
      return { pro: false };
    }
  }, []);

  // On mount: load current user from /me if we have a token; refresh pro from email if we have one
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const t = readLS(TOKEN_KEY);
      if (t) {
        try {
          const res = await fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${t}` } });
          if (res.ok) {
            const u = await res.json();
            if (!cancelled) {
              setUser(u);
              setEmail(u.email);
              writeLS(EMAIL_KEY, u.email);
            }
          } else if (res.status === 401) {
            // Stale token — clear it
            writeLS(TOKEN_KEY, null);
            if (!cancelled) setToken(null);
          }
        } catch { /* network — keep token, retry later */ }
      }
      await refresh();
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [refresh]);

  const saveEmail = useCallback((e) => { writeLS(EMAIL_KEY, e); setEmail(e); }, []);
  const clearEmail = useCallback(() => {
    writeLS(EMAIL_KEY, null);
    setEmail(null);
    setPro(false);
    setExpiresAt(null);
  }, []);

  const _setSession = useCallback((u, t) => {
    setUser(u);
    setToken(t);
    writeLS(TOKEN_KEY, t);
    setEmail(u.email);
    writeLS(EMAIL_KEY, u.email);
  }, []);

  const register = useCallback(async (emailVal, password) => {
    const res = await fetch(`${API}/auth/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: emailVal, password }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(formatApiError(data.detail));
    _setSession(data.user, data.token);
    await refresh(data.user.email);
    return data;
  }, [_setSession, refresh]);

  const login = useCallback(async (emailVal, password) => {
    const res = await fetch(`${API}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: emailVal, password }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(formatApiError(data.detail));
    _setSession(data.user, data.token);
    await refresh(data.user.email);
    return data;
  }, [_setSession, refresh]);

  const logout = useCallback(() => {
    writeLS(TOKEN_KEY, null);
    writeLS(EMAIL_KEY, null);
    setToken(null);
    setUser(null);
    setEmail(null);
    setPro(false);
    setExpiresAt(null);
  }, []);

  return (
    <ProContext.Provider
      value={{
        pro,
        email,
        expiresAt,
        loading,
        user,
        token,
        openUpgrade: () => setUpgradeOpen(true),
        closeUpgrade: () => setUpgradeOpen(false),
        upgradeOpen,
        refresh,
        saveEmail,
        clearEmail,
        register,
        login,
        logout,
      }}
    >
      {children}
    </ProContext.Provider>
  );
}

export const useProState = () => useContext(ProContext);
